import { useCallback } from 'react'

import { useAuthInfo } from '@propelauth/react'
import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { pursuitSchemasQueryKeys } from '@/hooks/pursuits/schemas/types'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { ExtendedJsonSchema } from '@/types/extended-schema'

interface UsePursuitSchemasArgs<T> {
  orgId: UUID | ''
  select?: (schema: PaginatedResponse<ExtendedJsonSchema>) => T
}

const fetchPursuitSchemas = async (
  orgId: UUID | '',
  accessToken: string,
): Promise<PaginatedResponse<ExtendedJsonSchema>> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/pursuit-schemas`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
  return (await res.json()) as PaginatedResponse<ExtendedJsonSchema>
}

export const usePursuitSchemas = <T = ExtendedJsonSchema[]>({
  orgId,
  select,
}: UsePursuitSchemasArgs<T>) => {
  const { accessToken } = useAuthInfo()

  const selectFn = useCallback(
    (data: PaginatedResponse<ExtendedJsonSchema>) => {
      if (select) {
        return select(data)
      }
      return data.data as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: pursuitSchemasQueryKeys.organization({ orgId }),
    queryFn: () => {
      if (!accessToken) {
        throw new Error('No access token provided')
      }
      return fetchPursuitSchemas(orgId, accessToken)
    },
    enabled: Boolean(orgId) && Boolean(accessToken),
    select: selectFn,
  })
}

interface UseLatestPursuitSchemaArgs {
  orgId: UUID | ''
}

export const selectLatestSchema = (
  data: PaginatedResponse<ExtendedJsonSchema>,
): ExtendedJsonSchema | undefined => {
  const sorted = data.data.sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })
  return sorted[0]
}

export const useLatestPursuitSchema = ({
  orgId,
}: UseLatestPursuitSchemaArgs) => {
  return usePursuitSchemas({
    orgId,
    select: selectLatestSchema,
  })
}

const selectLatestSchemaId = (
  data: PaginatedResponse<ExtendedJsonSchema>,
): UUID | undefined => {
  const latestSchema = selectLatestSchema(data)
  return latestSchema?.id
}

interface UseLatestPursuitSchemaIdArgs {
  orgId: UUID | ''
}

export const useLatestPursuitSchemaId = ({
  orgId,
}: UseLatestPursuitSchemaIdArgs) => {
  return usePursuitSchemas({
    orgId,
    select: selectLatestSchemaId,
  })
}

const selctHasAtleastOneSchema = (
  data: PaginatedResponse<ExtendedJsonSchema>,
): boolean => {
  return data.data.length > 0
}

interface UseOrganizationHasAtleastOnePursuitSchemaArgs {
  orgId: UUID | ''
}

export const useOrganizationHasAtleastOnePursuitSchema = ({
  orgId,
}: UseOrganizationHasAtleastOnePursuitSchemaArgs) => {
  return usePursuitSchemas({
    orgId,
    select: selctHasAtleastOneSchema,
  })
}
